import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Sprout, CloudRain, Bug, Shovel, Moon, RotateCcw, BookOpen, Palette,
  X, Star, Crown, Sword, Music, Heart, ChevronLeft, Home,
  Smile, Meh, Skull, Play, HelpCircle, Sparkles, Menu, TreeDeciduous,
  AlertTriangle, Volume2, VolumeX, Loader2, Languages, ChevronDown, Check
} from 'lucide-react';

// --- CSS Styles ---
const styles = `
  @keyframes refill { 0% { height: 0%; opacity: 0.5; } 100% { height: 100%; opacity: 1; } }
  @keyframes lookAround { 0%, 45% { transform: scaleX(1); } 50%, 95% { transform: scaleX(-1); } 100% { transform: scaleX(1); } }
  @keyframes spin-fast { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } 100% { transform: rotate(360deg) scale(0); opacity: 0; } }
  @keyframes flash-bang { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }
  @keyframes pop-in { 0% { transform: scale(0); opacity: 0; } 80% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
  @keyframes sunburst { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02, 0.98) translateY(2px); } }
  @keyframes aggressive-shake { 0% { transform: translate(1px, 1px) rotate(0deg); } 10% { transform: translate(-1px, -2px) rotate(-1deg); } 20% { transform: translate(-3px, 0px) rotate(1deg); } 30% { transform: translate(3px, 2px) rotate(0deg); } 40% { transform: translate(1px, -1px) rotate(1deg); } 50% { transform: translate(-1px, 2px) rotate(-1deg); } 60% { transform: translate(-3px, 1px) rotate(0deg); } 70% { transform: translate(3px, 1px) rotate(-1deg); } 80% { transform: translate(-1px, -1px) rotate(1deg); } 90% { transform: translate(1px, 2px) rotate(0deg); } 100% { transform: translate(1px, -2px) rotate(-1deg); } }
  @keyframes red-aura-pulse { 0%, 100% { filter: drop-shadow(0 0 0px rgba(255,0,0,0)); } 50% { filter: drop-shadow(0 0 15px rgba(255, 0, 0, 0.8)); } }
  @keyframes gold-glow { 0%, 100% { filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5)); } 50% { filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.9)); } }
  @keyframes fall-down { 0% { transform: translateY(-100px) rotate(0deg); opacity: 0; } 20% { opacity: 1; } 100% { transform: translateY(400px) rotate(360deg); opacity: 0; } }
  @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  @keyframes fall-spin { 0% { transform: translateY(-600px) rotate(0deg) scale(0.5); opacity: 0; } 20% { opacity: 1; } 100% { transform: translateY(0) rotate(1080deg) scale(1); opacity: 1; } }
  @keyframes pulse-ring { 0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(100, 200, 255, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(100, 200, 255, 0); } 100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(100, 200, 255, 0); } }

  .animate-refill { animation: refill 1.5s linear forwards; }
  .animate-look-around { animation: lookAround 6s infinite; }
  .animate-pest-move { animation: lookAround 2s infinite; }
  .animate-spin-vanish { animation: spin-fast 2s ease-in forwards; }
  .animate-flash { animation: flash-bang 2.5s ease-in-out forwards; }
  .animate-pop-in { animation: pop-in 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
  .animate-sunburst { animation: sunburst 10s linear infinite; }
  .animate-breathe { animation: breathe 3s ease-in-out infinite; }
  .animate-shake-hard { animation: aggressive-shake 0.5s linear infinite; }
  .animate-red-aura { animation: red-aura-pulse 2s ease-in-out infinite; }
  .animate-gold-aura { animation: gold-glow 3s ease-in-out infinite; }
  .animate-fall-down { animation: fall-down 3s linear forwards; }
  .animate-float { animation: float-slow 3s ease-in-out infinite; }
  .animate-fall-spin { animation: fall-spin 3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
  .animate-pulse-ring { animation: pulse-ring 2s infinite; }

  .game-container-bg {
    background-size: cover;
    background-position: center bottom;
    background-repeat: no-repeat;
    transition: background-image 0.5s ease-in-out;
  }
  .wave-bg {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='0.2' d='M0,160L48,176C96,192,192,224,288,224C384,224,480,192,576,165.3C672,139,768,117,864,128C960,139,1056,181,1152,197.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E");
    background-size: cover; background-position: bottom;
  }
  .scrollbar-hide::-webkit-scrollbar { display: none; }
  .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
`;
const LANG_OPTIONS = [
  { code: 'TH', label: 'ไทย', flag: '🇹🇭' },
  { code: 'EN', label: 'English', flag: '🇬🇧' },
  { code: 'KR', label: '한국어', flag: '🇰🇷' },
  { code: 'CN', label: '中文', flag: '🇨🇳' }
];
// --- Localization Data ---
const TEXT_CONTENT = {
  TH: {
    title: "ตำนานดอกกุหลาบ",
    subtitle: "เกมปลูกกุหลาบมัทนา",
    loading: "กำลังเตรียมดิน...",
    continue: "เล่นต่อจากเดิม",
    newGame: "เริ่มเกมใหม่",
    resetConfirm: "เริ่มใหม่ไหม? ข้อมูลการเลี้ยงรอบนี้จะหายไปนะ (แต่สมุดสะสมยังอยู่)",
    soundHint: "อย่าลืมเปิดเสียงนะ 🔊",
    footer: "หอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่",
    skipIntro: "ข้าม Intro",
    next: "ไปต่อ",
    helpPlant: "ช่วยปลูกมัทนาที! 🌱",
    tutorialTitle: "วิธีเล่นเกมปลูกต้นมัทนา",
    tutorialDesc: "มาช่วยกันปลูกต้นไม้ให้โตเถอะ!",
    t1_title: "1. ดูแลต้นไม้", t1_desc: "กดปุ่ม รดน้ำ ใส่ปุ๋ย พรวนดิน เพื่อให้ต้นไม้โตไวๆ",
    t2_title: "ข้อควรระวัง!", t2_desc: "อย่าเติมเยอะเกินขีดสีแดงนะ ไม่งั้นต้นไม้จะอึดอัดและเลือดลด!",
    t3_title: "2. กำจัดแมลง", t3_desc: "ถ้ามีแมลงมาเกาะ ให้รีบเอานิ้วจิ้มไล่ไปเลย!",
    t4_title: "3. รักษาชีวิต (HP)", t4_desc: "ดูแลให้ดี อย่าให้เลือดหมดนะ",
    t5_title: "4. เป้าหมายสูงสุด", t5_desc: "พอโตครบ 100% กดปุ่มพระจันทร์ เพื่อดูร่างที่แท้จริง!",
    btnStart: "เข้าใจแล้ว! เริ่มเลย",
    gallery: "สมุดสะสม",
    customize: "ตกแต่ง",
    bg: "ฉากหลัง",
    pot: "กระถาง",
    menu: "เมนูหลัก",
    home: "หน้าแรก",
    backGames: "ฟังนิทาน",
    howTo: "วิธีเล่น",
    story: "ดูเนื้อเรื่อง",
    soundOn: "เปิดเสียง",
    soundOff: "ปิดเสียง",
    water: "น้ำ",
    fertilizer: "ปุ๋ย",
    soil: "ดิน",
    action_water: "รดน้ำ",
    action_fertilizer: "ใส่ปุ๋ย",
    action_soil: "พรวนดิน",
    msg_overload_w: "น้ำท่วมแล้ว! (เยอะไป)",
    msg_overload_f: "เค็มปี๋เลย! (เยอะไป)",
    msg_overload_s: "แน่นอึดอัด! (เยอะไป)",
    msg_good_w: "สดชื่นจัง~",
    msg_good_f: "อร่อยมาก!",
    msg_good_s: "รากเดินสบาย~",
    msg_pest: "ช่วยด้วย! แมลง!",
    msg_pest_hit: "โอ๊ย! เจ็บจัง",
    msg_pest_kill: "ขอบคุณนะ!",
    msg_levelup: "✨ โตขึ้นแล้ว!",
    btn_reset: "เริ่มเลี้ยงใหม่ ↻",
    moon_wish: "กดเพื่อขอพร\nพระจันทร์",
    intro1_t: "ณ สวรรค์ชั้นฟ้า...", intro1_d: "สุเทษณ์เทพบุตร หลงรักนางฟ้าแสนสวย มัทนา...",
    intro2_t: "แต่รักไม่สมหวัง", intro2_d: "...แต่มัทนาไม่ได้รักตอบ... ทำให้สุเทษณ์โกรธมาก!",
    intro3_t: "คำสาปแห่งสวรรค์", intro3_d: "\"ข้าขอสาปเจ้า! ให้ลงไปเกิดเป็นดอกกุหลาบในโลกมนุษย์!\"",
    intro4_t: "ร่วงหล่นสู่พื้นดิน", intro4_d: "...มัทนาจึงกลายเป็นเมล็ดพันธุ์ ร่วงหล่นลงมา... รอคอยคนใจดีมาปลูก",
    langName: "ไทย"
  },
  EN: {
    title: "Legend of the Rose",
    subtitle: "Grow Mattana",
    loading: "Preparing soil...",
    continue: "Continue",
    newGame: "New Game",
    resetConfirm: "Restart? Current progress will be lost (Gallery saved).",
    soundHint: "Sound On Recommended 🔊",
    footer: "National Library of Chiang Mai",
    skipIntro: "Skip",
    next: "Next",
    helpPlant: "Help Plant Mattana! 🌱",
    tutorialTitle: "How to Play",
    tutorialDesc: "Let's grow the tree together!",
    t1_title: "1. Care", t1_desc: "Water, Fertilize, and Soil to help it grow.",
    t2_title: "Caution!", t2_desc: "Don't overfill past the red line! It hurts the plant.",
    t3_title: "2. Pests", t3_desc: "Tap pests to chase them away!",
    t4_title: "3. Health (HP)", t4_desc: "Keep HP above 0.",
    t5_title: "4. Goal", t5_desc: "Reach 100% and tap the Moon to reveal true form!",
    btnStart: "Got it! Start",
    gallery: "Gallery",
    customize: "Decor",
    bg: "Background",
    pot: "Pot",
    menu: "Menu",
    home: "Home",
    backGames: "Story",
    howTo: "Guide",
    story: "Replay Story",
    soundOn: "Sound On",
    soundOff: "Mute",
    water: "Water",
    fertilizer: "Fertilizer",
    soil: "Soil",
    action_water: "Water",
    action_fertilizer: "Fertilize",
    action_soil: "Till Soil",
    msg_overload_w: "Flooded! (Too much)",
    msg_overload_f: "Too Salty! (Too much)",
    msg_overload_s: "Suffocating! (Too much)",
    msg_good_w: "Refreshing~",
    msg_good_f: "Yummy!",
    msg_good_s: "Comfy~",
    msg_pest: "Help! Pests!",
    msg_pest_hit: "Ouch!",
    msg_pest_kill: "Thanks!",
    msg_levelup: "✨ Leveled Up!",
    btn_reset: "Restart ↻",
    moon_wish: "Make a Wish",
    intro1_t: "In Heaven...", intro1_d: "Suthep loved the beautiful angel Mattana...",
    intro2_t: "Unrequited Love", intro2_d: "...but she rejected him... making him furious!",
    intro3_t: "The Curse", intro3_d: "\"I curse you! Be reborn as a rose on Earth!\"",
    intro4_t: "Fallen to Earth", intro4_d: "...Mattana became a seed... waiting for someone kind.",
    langName: "English"
  },
  KR: {
    title: "장미의 전설",
    subtitle: "맛타나 장미 키우기",
    loading: "흙을 준비하는 중...",
    continue: "이어하기",
    newGame: "새 게임",
    resetConfirm: "다시 시작하시겠습니까? 현재 진행 상황이 사라집니다 (도감은 유지됩니다)",
    soundHint: "소리를 켜주세요 🔊",
    footer: "치앙마이 국립도서관",
    skipIntro: "건너뛰기",
    next: "다음",
    helpPlant: "맛타나를 심어주세요! 🌱",
    tutorialTitle: "게임 방법",
    tutorialDesc: "맛타나 나무를 함께 키워봐요!",
    t1_title: "1. 나무 돌보기", t1_desc: "물주기, 비료주기, 흙갈이 버튼을 눌러 나무를 성장시키세요.",
    t2_title: "주의사항!", t2_desc: "빨간 선을 넘지 않게 조심하세요! 나무가 아파합니다.",
    t3_title: "2. 해충 퇴치", t3_desc: "벌레가 나타나면 터치해서 쫓아내세요!",
    t4_title: "3. 생명력 (HP)", t4_desc: "HP가 0이 되지 않도록 잘 관리해주세요.",
    t5_title: "4. 최종 목표", t5_desc: "100% 성장하면 달님 버튼을 눌러 진정한 모습을 확인하세요!",
    btnStart: "알겠어요! 시작",
    gallery: "도감",
    customize: "꾸미기",
    bg: "배경",
    pot: "화분",
    menu: "메인 메뉴",
    home: "홈",
    backGames: "이야기 듣기",
    howTo: "게임 방법",
    story: "이야기 다시보기",
    soundOn: "소리 켜기",
    soundOff: "음소거",
    water: "물",
    fertilizer: "비료",
    soil: "흙",
    action_water: "물주기",
    action_fertilizer: "비료주기",
    action_soil: "흙갈이",
    msg_overload_w: "홍수 났어요! (너무 많음)",
    msg_overload_f: "너무 짜요! (너무 많음)",
    msg_overload_s: "숨막혀요! (너무 많음)",
    msg_good_w: "상쾌해요~",
    msg_good_f: "맛있어요!",
    msg_good_s: "편안해요~",
    msg_pest: "도와줘요! 벌레!",
    msg_pest_hit: "아야! 아파요",
    msg_pest_kill: "고마워요!",
    msg_levelup: "✨ 자라났어요!",
    btn_reset: "다시 키우기 ↻",
    moon_wish: "소원 빌기",
    intro1_t: "천상계에서...", intro1_d: "수테슨 신은 아름다운 천사 맛타나를 사랑했습니다...",
    intro2_t: "이루어지지 않은 사랑", intro2_d: "...하지만 맛타나는 거절했고... 수테슨은 분노했습니다!",
    intro3_t: "천상의 저주", intro3_d: "\"너를 인간 세상의 장미꽃으로 태어나게 저주하노라!\"",
    intro4_t: "땅으로 떨어지다", intro4_d: "...맛타나는 씨앗이 되어 떨어졌습니다... 누군가 심어주길 기다리며",
    langName: "한국어"
  },
  CN: {
    title: "玫瑰传说",
    subtitle: "种植马塔纳",
    loading: "正在准备土壤...",
    continue: "继续游戏",
    newGame: "新游戏",
    resetConfirm: "重新开始？当前的进度将会丢失（图鉴会保留）。",
    soundHint: "建议开启声音 🔊",
    footer: "清迈国家图书馆",
    skipIntro: "跳过",
    next: "下一步",
    helpPlant: "帮帮马塔纳！🌱",
    tutorialTitle: "游戏指南",
    tutorialDesc: "让我们一起把树养大！",
    t1_title: "1. 照顾", t1_desc: "浇水、施肥、松土，让它快快长大。",
    t2_title: "注意！", t2_desc: "不要超过红线！这会伤害植物。",
    t3_title: "2. 害虫", t3_desc: "点击害虫赶走它们！",
    t4_title: "3. 生命值 (HP)", t4_desc: "保持生命值大于 0。",
    t5_title: "4. 目标", t5_desc: "达到 100% 后点击月亮揭示真身！",
    btnStart: "明白了！开始",
    gallery: "图鉴",
    customize: "装饰",
    bg: "背景",
    pot: "花盆",
    menu: "菜单",
    home: "主页",
    backGames: "听故事",
    howTo: "玩法",
    story: "重看故事",
    soundOn: "开启声音",
    soundOff: "静音",
    water: "水",
    fertilizer: "肥料",
    soil: "土",
    action_water: "浇水",
    action_fertilizer: "施肥",
    action_soil: "松土",
    msg_overload_w: "发大水了！（太多了）",
    msg_overload_f: "太咸了！（太多了）",
    msg_overload_s: "太闷了！（太多了）",
    msg_good_w: "真清爽~",
    msg_good_f: "真好吃！",
    msg_good_s: "真舒服~",
    msg_pest: "救命！有虫子！",
    msg_pest_hit: "哎哟！好痛",
    msg_pest_kill: "谢谢！",
    msg_levelup: "✨ 升级了！",
    btn_reset: "重新种植 ↻",
    moon_wish: "向月亮许愿",
    intro1_t: "在天界...", intro1_d: "苏特恩深爱着美丽的仙女马塔纳...",
    intro2_t: "无果的爱", intro2_d: "...但她拒绝了他... 让他勃然大怒！",
    intro3_t: "天界的诅咒", intro3_d: "“我诅咒你！下凡变成一朵玫瑰花！”",
    intro4_t: "坠落凡间", intro4_d: "...马塔纳变成了一颗种子... 等待好心人种下。",
    langName: "中文"
  }
};

const getEndingsData = (lang) => {
    // Helper to select text based on lang code
    const getText = (th, en, kr, cn) => {
        if (lang === 'EN') return en;
        if (lang === 'KR') return kr;
        if (lang === 'CN') return cn;
        return th;
    };

    return {
        ROYAL: { 
            id: 'ROYAL', 
            name: getText('มัทนาเทวี', 'Queen Mattana', '맛타나 여왕', '马塔纳女王'),
            desc: getText('นางผู้เลอโฉม', 'The Beauty', '고귀한 아름다움', '绝世美人'),
            img: 'ROYAL.webp', 
            icon: <Crown size={48} className="text-yellow-500" /> 
        },
        FAIRY: { 
            id: 'FAIRY', 
            name: getText('ภูตบุปผา', 'Flower Fairy', '꽃의 요정', '花仙子'),
            desc: getText('จิตวิญญาณแห่งป่า', 'Forest Spirit', '숲의 영혼', '森林之灵'),
            img: 'FAIRY.webp', 
            icon: <Sprout size={48} className="text-green-400" /> 
        },
        WARRIOR: { 
            id: 'WARRIOR', 
            name: getText('กุหลาบหนาม', 'Thorn Warrior', '가시 전사', '荆棘战士'),
            desc: getText('ผู้แข็งแกร่ง', 'The Strong', '강인한 전사', '坚强的战士'),
            img: 'WARRIOR.webp', 
            icon: <Sword size={48} className="text-red-500" /> 
        },
        MODERN: { 
            id: 'MODERN', 
            name: getText('เน็ตไอดอล', 'Net Idol', '아이돌 스타', '网络偶像'),
            desc: getText('ขวัญใจยุคใหม่', 'Modern Pop Star', '현대의 인기인', '现代宠儿'),
            img: 'MODERN.webp', 
            icon: <Music size={48} className="text-pink-400" /> 
        },
        NORMAL: { 
            id: 'NORMAL', 
            name: getText('หญิงสาว', 'Village Girl', '평범한 소녀', '普通少女'),
            desc: getText('ชาวบ้านธรรมดา', 'Simple Life', '순수한 마음', '平凡生活'),
            img: 'ROYAL.webp', 
            icon: <Heart size={48} className="text-gray-400" /> 
        },
        DEAD: { 
            id: 'DEAD', 
            name: getText('กุหลาบเฉา', 'Withered Rose', '시든 장미', '枯萎的玫瑰'),
            desc: getText('ขาดการดูแล', 'Neglected', '관리 부족', '缺乏照顾'),
            img: 'dead.webp', 
            icon: <X size={48} className="text-gray-600" /> 
        },
    };
};

// --- Game Data ---
const ASSET_PATH = '/assets/MattanaGame';

const PRELOAD_ASSETS = {
  images: [
    'bg_heaven_clear.webp', 'bg_heaven_storm.webp', 'bg_garden_start.webp',
    'char_suthep_love.png', 'char_mattana_angel.png', 'char_suthep_angry.png', 'char_mattana_sad.png', 'item_magic_seed.png',
    'seed.webp', 'sprout.webp', 'bush.webp', 'bloom.webp', 'dead.webp', 'pest.webp',
    'Clay Pot.webp', 'Ceramic Pot.webp', 'Gold Pot.webp',
    'Default.webp', 'Forest.webp', 'Sky.webp', 'School.webp',
    'ROYAL.webp', 'FAIRY.webp', 'WARRIOR.webp', 'MODERN.webp'
  ],
  audio: [
    'voice_intro_1.mp3', 'voice_intro_2.mp3', 'voice_intro_3.mp3', 'voice_intro_4.mp3',
    // Note: If you have voice files for other languages, add them here.
    // For now, code handles fallback if files are missing (silence).
    'bgm_garden.mp3', 'water.mp3', 'fertilizer.mp3', 'soil.mp3',
    'pop.mp3', 'alert.mp3', 'magic.mp3', 'win.mp3'
  ]
};

const THEMES = [
  { id: 'default', name: { TH: 'สวนยามเช้า', EN: 'Morning Garden', KR: '아침 정원', CN: '清晨花园' }, file: 'Default.webp' },
  { id: 'forest', name: { TH: 'ป่าหิมพานต์', EN: 'Mythic Forest', KR: '신비의 숲', CN: '神秘森林' }, file: 'Forest.webp' },
  { id: 'sky', name: { TH: 'วิมานเมฆ', EN: 'Cloud Palace', KR: '구름 위', CN: '云端' }, file: 'Sky.webp' },
  { id: 'school', name: { TH: 'รั้วโรงเรียน', EN: 'High School', KR: '학교', CN: '学校' }, file: 'School.webp' },
];

const POTS = [
  { id: 'clay', name: { TH: 'ดินเผา', EN: 'Clay Pot', KR: '토기', CN: '陶盆' }, file: 'Clay Pot.webp' },
  { id: 'ceramic', name: { TH: 'เซรามิกขาว', EN: 'Ceramic', KR: '세라믹', CN: '陶瓷' }, file: 'Ceramic Pot.webp' },
  { id: 'gold', name: { TH: 'ทองคำ', EN: 'Gold', KR: '황금', CN: '金盆' }, file: 'Gold Pot.webp' },
];

// --- Sub-component: Typewriter Effect ---
const TypewriterText = ({ text, speed = 30, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(prev => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);
  return <span>{displayedText}</span>;
};

// --- Sub-component: Story Intro ---
const StoryIntro = ({ onFinish, onSkip, assetPath, playSound, language }) => {
  const [slide, setSlide] = useState(0);
  const voiceRef = useRef(null);
  const t = TEXT_CONTENT[language];

  // Logic เลือกไฟล์เสียงตามภาษา (Fallback to EN/TH if specific lang voice missing is not handled here, assume files exist or browser handles missing 404 silently)
  const getVoiceFile = (baseName) => {
      if (language === 'KR') return `${baseName}_kr.mp3`;
      if (language === 'CN') return `${baseName}_cn.mp3`; // Ensure these files exist or remove this line
      if (language === 'EN') return `${baseName}_en.mp3`; // Ensure these files exist or remove this line
      return `${baseName}.mp3`; // Default TH
  };

  const stories = [
    {
      bgFile: "bg_heaven_clear.webp",
      voiceFile: getVoiceFile("voice_intro_1"),
      chars: [
        { file: "char_suthep_love.png", style: "w-[100px] bottom-[1%] left-[55%] -translate-x-1/2 z-10 animate-float animate-breathe origin-bottom" },
        { file: "char_mattana_angel.png", style: "w-[120px] bottom-[0.1%] -left-15 z-20 animate-float animate-gold-aura animate-breathe origin-bottom" }
      ],
      title: t.intro1_t,
      desc: t.intro1_d,
      color: "text-indigo-800"
    },
    {
      bgFile: "bg_heaven_clear.webp",
      voiceFile: getVoiceFile("voice_intro_2"),
      chars: [
        { file: "char_suthep_angry.png", style: "w-[120px] bottom-[1%] left-[55%] -translate-x-1/2 z-10 animate-float animate-breathe origin-bottom" },
        { file: "char_mattana_sad.png", style: "w-[100px] bottom-[0.1%] -left-15 z-20 animate-float animate-gold-aura animate-breathe origin-bottom" }
      ],
      title: t.intro2_t,
      desc: t.intro2_d,
      color: "text-red-700"
    },
    {
      bgFile: "bg_heaven_storm.webp",
      voiceFile: getVoiceFile("voice_intro_3"),
      chars: [
        { file: "char_suthep_angry.png", style: "w-64 bottom-0 left-1/2 -translate-x-1/2 z-20 animate-shake-hard animate-red-aura" }
      ],
      title: t.intro3_t,
      desc: t.intro3_d,
      isFlash: true,
      color: "text-red-900"
    },
    {
      bgFile: "bg_garden_start.webp",
      voiceFile: getVoiceFile("voice_intro_4"),
      chars: [
        { file: "item_magic_seed.png", style: "w-24 top-[30%] left-1/2 -translate-x-1/2 z-20 animate-fall-spin drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]" }
      ],
      title: t.intro4_t,
      desc: t.intro4_d,
      color: "text-green-800"
    }
  ];

  const stopVoice = () => {
    if (voiceRef.current) {
      voiceRef.current.pause();
      voiceRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    stopVoice();
    const currentStory = stories[slide];
    if (currentStory.voiceFile) {
      const audio = new Audio(`${assetPath}/${currentStory.voiceFile}`);
      audio.volume = 0.8;
      voiceRef.current = audio;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => { console.log("Audio autoplay prevented or file missing:", error); });
      }
    }
    return () => stopVoice();
  }, [slide, assetPath, language]);

  const handleNext = () => {
    if (slide < stories.length - 1) {
      setSlide(s => s + 1);
    } else {
      stopVoice();
      onFinish();
    }
  };

  const handleSkipAction = () => {
    stopVoice();
    onSkip();
  };

  const current = stories[slide];

  return (
    <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] h-[850px] max-h-[90vh] sm:rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col items-center border-4 border-zinc-800 bg-black">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center transition-all duration-700 ease-in-out transform scale-105" style={{ backgroundImage: `url('${assetPath}/${current.bgFile}')` }}></div>
        {current.isFlash && <div className="absolute inset-0 bg-white/80 animate-flash pointer-events-none z-30"></div>}
        <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
          <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full animate-ping"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-yellow-300 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 left-1/2 w-2 h-2 bg-white rounded-full animate-bounce"></div>
        </div>
        <button onClick={handleSkipAction} className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 backdrop-blur-md px-4 py-2 rounded-full text-xs font-bold text-white border border-white/30 z-50 flex items-center gap-1 transition-all active:scale-95">{t.skipIntro} <ChevronLeft className="rotate-180" size={12} /></button>
        <div className="flex-1 w-full relative overflow-hidden">
          {current.chars && current.chars.map((char, idx) => (
            <img key={`${slide}-${idx}`} src={`${assetPath}/${char.file}`} alt="character" className={`absolute object-contain transition-all duration-500 ${char.style}`} onError={(e) => e.target.style.display = 'none'} />
          ))}
        </div>
        <div className="w-full bg-gradient-to-t from-black via-black/90 to-transparent p-6 pt-16 z-40 text-center relative mt-auto">
          <div className="bg-white/95 backdrop-blur-sm p-5 rounded-2xl shadow-xl border-4 border-indigo-200 animate-pop-in mb-4 min-h-[100px] flex flex-col justify-center">
            <h2 className={`text-xl font-bold mb-2 flex items-center justify-center gap-2 ${current.color}`}>
              {slide === 0 ? <Heart size={20} className="text-pink-500 animate-pulse" /> : slide === 2 ? <CloudRain size={20} className="text-purple-600 animate-bounce" /> : <Sparkles size={20} className="text-yellow-500 animate-spin-slow" />}
              {current.title}
            </h2>
            <p className="text-gray-700 text-sm font-medium leading-relaxed"><TypewriterText text={current.desc} speed={40} /></p>
          </div>
          <button onClick={handleNext} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-lg font-bold py-3 rounded-xl shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 border border-white/20">
            {slide === stories.length - 1 ? <span>{t.helpPlant}</span> : <span>{t.next} <span className="text-xs opacity-70">▶</span></span>}
          </button>
          <div className="flex justify-center gap-2 mt-4">
            {stories.map((_, idx) => (<div key={idx} className={`h-1.5 rounded-full transition-all duration-500 ${idx === slide ? 'bg-white w-8 shadow-[0_0_10px_white]' : 'bg-white/20 w-2'}`}></div>))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Landing/Loading Screen Component ---
// --- Landing/Loading Screen Component ---
const LandingScreen = ({ onContinue, onNewGame, assetPath, language, setLanguage }) => {
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [hasSaveFile, setHasSaveFile] = useState(false);
  
  // เพิ่ม State สำหรับเปิด/ปิด Dropdown
  const [showLangMenu, setShowLangMenu] = useState(false);
  
  const t = TEXT_CONTENT[language];

  // ถ้าต้องการเปลี่ยน Logo ตามภาษา ทำได้ที่นี่
  const LIBRARY_LOGO_URL = `${assetPath}/logo.png`;

  useEffect(() => {
    const savedGame = localStorage.getItem('mattana_save_v9');
    setHasSaveFile(!!savedGame);

    let loadedCount = 0;
    if (!assetPath) { setLoaded(true); return; }

    const totalAssets = PRELOAD_ASSETS.images.length + PRELOAD_ASSETS.audio.length;
    const updateProgress = () => {
      loadedCount++;
      const percent = Math.floor((loadedCount / totalAssets) * 100);
      setProgress(percent);
      if (loadedCount >= totalAssets) {
        setTimeout(() => setLoaded(true), 500);
      }
    };

    PRELOAD_ASSETS.images.forEach(file => {
      const img = new Image(); img.src = `${assetPath}/${file}`;
      img.onload = updateProgress; img.onerror = updateProgress;
    });
    PRELOAD_ASSETS.audio.forEach(file => {
      const audio = new Audio(); audio.src = `${assetPath}/${file}`;
      audio.oncanplaythrough = updateProgress; audio.onerror = updateProgress;
      audio.load();
    });

    const timeout = setTimeout(() => { if (!loaded) setLoaded(true); }, 5000);
    return () => clearTimeout(timeout);
  }, [assetPath]);

  return (
    <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] h-[850px] max-h-[90vh] sm:rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col items-center justify-center border-4 border-sky-200 bg-white">

        <div className="absolute inset-0 bg-gradient-to-b from-sky-300 via-sky-100 to-green-200"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)', backgroundSize: '20px 20px'
        }}></div>

        <div className="absolute top-4 right-4 z-50 animate-pop-in">
          <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full overflow-hidden shadow-lg border-2 border-sky-200 flex items-center justify-center">
            <img src={LIBRARY_LOGO_URL} alt="Library Logo" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* --- Language Dropdown Button --- */}
        <div className="absolute top-4 left-4 z-50 animate-pop-in">
          <div className="relative">
            <button 
              onClick={() => setShowLangMenu(!showLangMenu)} 
              className="bg-white/80 hover:bg-white p-2 px-3 rounded-full shadow-md flex items-center gap-2 text-sm font-bold text-sky-800 border border-sky-100 transition-all"
            >
              <Languages size={18} /> 
              {/* แสดงชื่อภาษาปัจจุบัน */}
              {LANG_OPTIONS.find(l => l.code === language)?.label || language}
              <ChevronDown size={14} className={`transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showLangMenu && (
              <div className="absolute top-12 left-0 bg-white rounded-xl shadow-xl border border-sky-100 overflow-hidden w-40 animate-in fade-in zoom-in-95 duration-200 flex flex-col z-[60]">
                {LANG_OPTIONS.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLangMenu(false);
                    }}
                    className={`text-left px-4 py-3 text-sm flex items-center justify-between hover:bg-sky-50 transition-colors
                      ${language === lang.code ? 'text-sky-700 font-bold bg-sky-50/50' : 'text-gray-600'}
                    `}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg">{lang.flag}</span> {lang.label}
                    </span>
                    {language === lang.code && <Check size={14} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center w-full px-6">
          <div className="mb-8 animate-float">
            <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-green-300 mb-4 relative overflow-hidden mx-auto">
              <div className="absolute inset-0 bg-green-50"></div>
              <Sprout size={64} className="text-green-500 relative z-10" />
            </div>
            <h1 className="text-4xl font-bold text-sky-700 drop-shadow-sm filter">{t.title}</h1>
            <p className="text-sm text-sky-600 mt-2 font-bold bg-white/60 px-4 py-1 rounded-full backdrop-blur-sm inline-block">{t.subtitle}</p>
          </div>

          {!loaded ? (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-sky-800 mb-2 font-bold uppercase tracking-wider">
                <span>{t.loading}</span><span>{progress}%</span>
              </div>
              <div className="h-4 bg-white rounded-full overflow-hidden border border-sky-300 shadow-inner">
                <div className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-300 ease-out" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="mt-4 flex justify-center"><Loader2 className="animate-spin text-sky-600" size={24} /></div>
            </div>
          ) : (
            <div className="animate-pop-in w-full flex flex-col gap-3 items-center">
              {hasSaveFile && (
                <button onClick={onContinue} className="w-full max-w-xs py-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white rounded-xl font-bold text-xl shadow-[0_4px_15px_rgba(251,146,60,0.4)] hover:scale-105 active:scale-95 transition-all animate-pulse-ring border-2 border-white/50 flex items-center justify-center gap-2">
                  <Play size={24} fill="currentColor" /> {t.continue}
                </button>
              )}

              <button onClick={onNewGame} className={`w-full max-w-xs py-3 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 active:scale-95 ${!hasSaveFile
                ? 'bg-gradient-to-r from-green-400 to-teal-500 text-white shadow-lg py-4 text-xl animate-pulse-ring'
                : 'bg-white/80 text-sky-700 hover:bg-white border-2 border-sky-200'}`}>
                {hasSaveFile ? <><RotateCcw size={20} /> {t.newGame}</> : <><Play size={24} fill="currentColor" /> {t.newGame}</>}
              </button>

              <p className="text-xs text-sky-700 mt-4 font-bold bg-white/40 px-3 py-1 rounded-lg">{t.soundHint}</p>
            </div>
          )}
        </div>

        <div className="absolute bottom-6 text-[10px] text-sky-800/50 font-mono">{t.footer}</div>
      </div>
    </div>
  );
};

// --- Main Component ---
// --- Main Component ---
// --- Main Component ---
const MattanaGame = () => {
  const [gameState, setGameState] = useState({ stage: 'seed', hp: 100, water: 50, fertilizer: 50, soil: 50, growth: 0, isNight: false, ending: null });
  const [activePests, setActivePests] = useState([]);
  const [collection, setCollection] = useState([]);
  const [config, setConfig] = useState({ theme: 'default', pot: 'clay' });
  const [view, setView] = useState('landing');
  const [message, setMessage] = useState("");
  const [cooldowns, setCooldowns] = useState({ water: false, fertilizer: false, soil: false });
  const [isTransforming, setIsTransforming] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  // --- Language State ---
  const [language, setLanguage] = useState('TH');
  // State สำหรับเปิด/ปิด Dropdown ในเมนูเกม
  const [showInGameLangMenu, setShowInGameLangMenu] = useState(false);

  const requestRef = useRef();
  const pestSpawnRef = useRef();
  const bgmRef = useRef(null);
  const sfxRef = useRef(null);
  const t = TEXT_CONTENT[language];
  const ENDINGS = getEndingsData(language);

  const unlockAudio = () => {
    const warmUp = new Audio(`${ASSET_PATH}/pop.mp3`);
    warmUp.volume = 0.0;
    warmUp.play().catch(() => { });
  };

  const handleContinueGame = () => {
    unlockAudio();
    const savedGame = localStorage.getItem('mattana_save_v9');
    if (savedGame) {
      setView('main');
      if (bgmRef.current && !isMuted) bgmRef.current.play().catch(() => { });
    } else {
      handleNewGame();
    }
  };

  const handleNewGame = () => {
    unlockAudio();
    localStorage.removeItem('mattana_save_v9');
    setGameState({ stage: 'seed', hp: 100, water: 50, fertilizer: 50, soil: 50, growth: 0, isNight: false, ending: null });
    setActivePests([]);
    setMessage("");
    setView('intro');
  };

  const playSound = (soundName) => {
    if (isMuted) return;
    if (sfxRef.current) {
      sfxRef.current.pause();
      sfxRef.current.currentTime = 0;
    }
    const audio = new Audio(`${ASSET_PATH}/${soundName}.mp3`);
    audio.volume = 0.6;
    sfxRef.current = audio;
    audio.play().catch(e => console.log("Audio play failed"));
  };

  useEffect(() => {
    if (!bgmRef.current) {
      bgmRef.current = new Audio(`${ASSET_PATH}/bgmgarden.mp3`);
      bgmRef.current.loop = true;
      bgmRef.current.volume = 0.3;
    }

    if (view === 'main' && !isMuted) {
      bgmRef.current.play().catch(e => console.log("BGM play failed:", e));
    } else {
      bgmRef.current.pause();
    }

    return () => {
      if (bgmRef.current) bgmRef.current.pause();
      if (sfxRef.current) sfxRef.current.pause();
    }
  }, [view, isMuted]);

  useEffect(() => {
    const savedGame = localStorage.getItem('mattana_save_v9');
    if (savedGame) setGameState(JSON.parse(savedGame));

    const savedCollection = localStorage.getItem('mattana_collection_v9');
    if (savedCollection) setCollection(JSON.parse(savedCollection));

    const savedConfig = localStorage.getItem('mattana_config_v9');
    if (savedConfig) setConfig(JSON.parse(savedConfig));

    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    localStorage.setItem('mattana_save_v9', JSON.stringify(gameState));
    localStorage.setItem('mattana_collection_v9', JSON.stringify(collection));
    localStorage.setItem('mattana_config_v9', JSON.stringify(config));
  }, [gameState, collection, config, isLoaded]);

  // --- Game Mechanics ---
  useEffect(() => {
    if (gameState.stage === 'human' || gameState.ending || view === 'tutorial' || view === 'intro' || view === 'landing' || isTransforming) return;
    const timer = setInterval(() => {
      setGameState(prev => {
        if (prev.hp <= 0) return { ...prev, ending: 'DEAD', stage: 'dead' };
        const isOverload = prev.water > 90 || prev.fertilizer > 90 || prev.soil > 90;
        const decayRate = isOverload ? { w: 2.0, f: 1.5, s: 1.0 } : { w: 0.3, f: 0.2, s: 0.1 };
        let passiveGrowth = (!isOverload && prev.water > 0 && prev.fertilizer > 0 && prev.soil > 0) ? 0.1 : 0;
        return {
          ...prev,
          water: Math.max(0, prev.water - decayRate.w),
          fertilizer: Math.max(0, prev.fertilizer - decayRate.f),
          soil: Math.max(0, prev.soil - decayRate.s),
          growth: Math.min(100, prev.growth + passiveGrowth)
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState.stage, gameState.ending, view, isTransforming]);

  useEffect(() => {
    if (gameState.stage === 'human' || gameState.ending || view === 'tutorial' || view === 'intro' || view === 'landing' || isTransforming) return;
    const SPAWN_INTERVAL = 3000;
    pestSpawnRef.current = setInterval(() => { if (Math.random() > 0.3) spawnPest(); }, SPAWN_INTERVAL);
    return () => clearInterval(pestSpawnRef.current);
  }, [gameState.stage, gameState.ending, view, isTransforming]);

  const spawnPest = () => { setActivePests(prev => [...prev, { id: Date.now(), x: Math.random() > 0.5 ? -10 : 110, y: Math.random() * 60, speed: 0.03 + Math.random() * 0.03 }]); };

  useEffect(() => {
    if (gameState.stage === 'human' || gameState.ending || view === 'tutorial' || view === 'intro' || view === 'landing' || isTransforming) return;
    const movePests = () => {
      setActivePests(prevPests => {
        if (prevPests.length === 0) return [];
        const nextPests = [];
        let damageTaken = 0;
        prevPests.forEach(pest => {
          const dx = 50 - pest.x; const dy = 50 - pest.y;
          if (Math.sqrt(dx * dx + dy * dy) < 5) damageTaken += 5;
          else nextPests.push({ ...pest, x: pest.x + Math.cos(Math.atan2(dy, dx)) * pest.speed, y: pest.y + Math.sin(Math.atan2(dy, dx)) * pest.speed });
        });
        if (damageTaken > 0) {
          setGameState(gs => ({ ...gs, hp: Math.max(0, gs.hp - damageTaken) }));
          setMessage(`${t.msg_pest_hit} (-${damageTaken})`);
          playSound('alert');
          setTimeout(() => setMessage(""), 2000);
        }
        return nextPests;
      });
      requestRef.current = requestAnimationFrame(movePests);
    };
    requestRef.current = requestAnimationFrame(movePests);
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState.stage, gameState.ending, view, isTransforming, language]);

  const killPest = (id) => {
    setActivePests(prev => prev.filter(p => p.id !== id));
    playSound('pop');
    setMessage(t.msg_pest_kill);
    setGameState(prev => ({ ...prev, hp: Math.min(100, prev.hp + 2), growth: Math.min(100, prev.growth + 3) }));
    setTimeout(() => setMessage(""), 2000);
  };

  const triggerCooldown = (action) => {
    setCooldowns(prev => ({ ...prev, [action]: true }));
    setTimeout(() => { setCooldowns(prev => ({ ...prev, [action]: false })); }, 1500);
  };

  const handleAction = (action) => {
    if (gameState.ending || cooldowns[action] || isTransforming) return;
    triggerCooldown(action);
    if (['water', 'fertilizer', 'soil'].includes(action)) playSound(action);
    if (action === 'moon') {
      if (gameState.growth >= 100 && gameState.stage === 'bloom') {
        setIsTransforming(true); setActivePests([]); playSound('magic');
        setTimeout(() => {
          setGameState(prev => {
            const endingID = determineEnding(prev);
            if (!collection.includes(endingID)) setCollection(c => [...c, endingID]);
            return { ...prev, isNight: true, stage: 'human', ending: endingID, hp: 100 };
          });
          playSound('win'); setIsTransforming(false);
        }, 2000);
      }
      return;
    }
    setGameState(prev => {
      let newState = { ...prev };
      let msg = "";
      const REFILL_AMOUNT = 10;
      switch (action) {
        case 'water':
          newState.water = Math.min(100, prev.water + REFILL_AMOUNT);
          newState.fertilizer = Math.max(0, prev.fertilizer - 3);
          if (newState.water > 90) { msg = t.msg_overload_w; newState.growth = Math.max(0, prev.growth - 5); newState.hp = Math.max(0, prev.hp - 10); } else { newState.growth = Math.min(100, prev.growth + 2); msg = t.msg_good_w; }
          break;
        case 'fertilizer':
          newState.fertilizer = Math.min(100, prev.fertilizer + REFILL_AMOUNT);
          newState.soil = Math.max(0, prev.soil - 3);
          if (newState.fertilizer > 90) { msg = t.msg_overload_f; newState.growth = Math.max(0, prev.growth - 5); newState.hp = Math.max(0, prev.hp - 10); } else { newState.growth = Math.min(100, prev.growth + 2); msg = t.msg_good_f; }
          break;
        case 'soil':
          newState.soil = Math.min(100, prev.soil + REFILL_AMOUNT);
          newState.water = Math.max(0, prev.water - 3);
          if (newState.soil > 90) { msg = t.msg_overload_s; newState.growth = Math.max(0, prev.growth - 5); newState.hp = Math.max(0, prev.hp - 10); } else { newState.growth = Math.min(100, prev.growth + 2); msg = t.msg_good_s; }
          break;
        default: break;
      }
      if (!newState.ending) {
        let nextStage = prev.stage;
        if (newState.growth < 30) nextStage = 'seed'; else if (newState.growth < 60) nextStage = 'sprout'; else if (newState.growth < 100) nextStage = 'bush'; else if (newState.growth >= 100) nextStage = 'bloom';
        if (nextStage !== prev.stage) { newState.stage = nextStage; if (newState.growth > prev.growth) { newState.hp = 100; msg = t.msg_levelup; playSound('pop'); } }
      }
      setMessage(msg); setTimeout(() => setMessage(""), 2000); return newState;
    });
  };

  const determineEnding = (stats) => {
    const { water, fertilizer, soil } = stats;
    if (water > 80 && fertilizer > 80 && soil > 80) return 'ROYAL';
    if (water >= fertilizer && water >= soil) return 'FAIRY';
    if (soil >= water && soil >= fertilizer) return 'WARRIOR';
    if (fertilizer >= water && fertilizer >= soil) return 'MODERN';
    return 'NORMAL';
  };

  const resetGame = () => {
    if (window.confirm(t.resetConfirm)) {
      localStorage.removeItem('mattana_save_v9');
      setGameState({ stage: 'seed', hp: 100, water: 50, fertilizer: 50, soil: 50, growth: 0, isNight: false, ending: null });
      setActivePests([]); setMessage("");
      if (sfxRef.current) { sfxRef.current.pause(); sfxRef.current.currentTime = 0; }
      setView('intro');
    }
  };

  const getCurrentBgStyle = () => {
    if (gameState.isNight) return { backgroundColor: '#1e1b4b' };
    const theme = THEMES.find(t => t.id === config.theme);
    const bgFile = theme ? theme.file : 'Default.webp';
    return { backgroundImage: `url('${ASSET_PATH}/${bgFile}')` };
  };

  const getCurrentPotFile = () => { const pot = POTS.find(p => p.id === config.pot); return pot ? pot.file : 'Clay Pot.webp'; };

  // --- Views ---
  if (view === 'landing') return (
    <LandingScreen
      onContinue={handleContinueGame}
      onNewGame={handleNewGame}
      assetPath={ASSET_PATH}
      language={language}
      setLanguage={setLanguage}
    />
  );

  if (view === 'intro') return (
    <StoryIntro
      assetPath={ASSET_PATH}
      playSound={playSound}
      language={language}
      onFinish={() => { playSound('pop'); setView('tutorial'); }}
      onSkip={() => { setView('tutorial'); }}
    />
  );

  if (view === 'tutorial') return (
    <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] bg-sky-100 flex flex-col items-center justify-center p-4 text-center relative overflow-hidden font-sans sm:rounded-3xl shadow-2xl h-[850px] max-h-[90vh]">
        <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full relative z-10 border-4 border-sky-300 flex flex-col h-full max-h-[80vh]">
          <h1 className="text-2xl font-bold text-sky-600 mb-2 flex-shrink-0">{t.tutorialTitle}</h1>
          <p className="text-gray-600 mb-4 text-sm flex-shrink-0">{t.tutorialDesc}</p>
          <div className="flex-1 overflow-y-auto scrollbar-hide space-y-3 text-left mb-4 px-2">
            <TutorialInfo icon={<CloudRain size={18} className="text-blue-500" />} bgColor="bg-blue-50 animate-pulse" title={t.t1_title} desc={t.t1_desc} />
            <TutorialInfo icon={<AlertTriangle size={18} className="text-orange-500" />} bgColor="bg-orange-50 border-orange-200" title={t.t2_title} desc={t.t2_desc} />
            <TutorialInfo icon={<Bug size={18} className="text-red-500 animate-bounce" />} bgColor="bg-red-50" title={t.t3_title} desc={t.t3_desc} />
            <TutorialInfo icon={<Heart size={18} className="text-pink-500 animate-pulse" />} bgColor="bg-pink-50" title={t.t4_title} desc={t.t4_desc} />
            <TutorialInfo icon={<Moon size={18} className="text-purple-500 animate-pulse" />} bgColor="bg-purple-50" title={t.t5_title} desc={t.t5_desc} />
          </div>
          <button onClick={() => { playSound('pop'); setView('main'); }} className="w-full bg-gradient-to-r from-sky-400 to-blue-500 text-white py-3 rounded-xl font-bold text-lg shadow-lg hover:scale-105 transition-transform flex items-center justify-center gap-2 flex-shrink-0"><Play size={24} fill="white" /> {t.btnStart}</button>
        </div>
      </div>
    </div>
  );

  if (view === 'gallery') return (
    <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] bg-amber-50 p-4 font-sans relative sm:rounded-3xl shadow-2xl h-[850px] max-h-[90vh] overflow-y-auto">
        <button onClick={() => setView('main')} className="absolute top-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-white/80 rounded-full shadow-md text-amber-800"><X size={20} /></button>
        <h2 className="text-2xl font-bold text-amber-800 flex justify-center items-center gap-2 mt-10 mb-6"><BookOpen /> {t.gallery}</h2>
        <div className="grid grid-cols-2 gap-4 pb-20">
          {Object.values(ENDINGS).filter(e => e.id !== 'DEAD').map((end) => {
            const isUnlocked = collection.includes(end.id);
            return (
              <div key={end.id} className={`p-4 rounded-xl border-2 flex flex-col items-center text-center ${isUnlocked ? 'bg-white border-amber-300' : 'bg-gray-200 border-gray-300 opacity-60'}`}>
                <div className="mb-2"> {isUnlocked ? <img src={`${ASSET_PATH}/${end.img}`} alt={end.name} className="w-20 h-20 object-contain drop-shadow-md" /> : <div className="text-4xl grayscale opacity-50">🔒</div>} </div>
                <div className="font-bold text-sm">{isUnlocked ? end.name : '???'}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  );

  if (view === 'customize') return (
    <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] bg-white p-4 font-sans relative sm:rounded-3xl shadow-2xl h-[850px] max-h-[90vh] overflow-y-auto">
        <button onClick={() => setView('main')} className="absolute top-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full shadow-md text-gray-800"><X size={20} /></button>
        <h2 className="text-2xl font-bold text-purple-800 flex justify-center items-center gap-2 mt-10 mb-6"><Palette /> {t.customize}</h2>
        <h3 className="font-bold mb-3 text-gray-700">{t.bg}</h3>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {THEMES.map(theme => (
            <button key={theme.id} onClick={() => setConfig({ ...config, theme: theme.id })} className={`p-1 rounded-lg border-2 overflow-hidden h-24 relative ${config.theme === theme.id ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-200'}`}>
              <img src={`${ASSET_PATH}/${theme.file}`} alt={theme.name[language]} className="w-full h-full object-cover" />
              <span className="absolute bottom-0 left-0 w-full bg-white/80 text-xs font-bold py-1">{theme.name[language]}</span>
            </button>
          ))}
        </div>
        <h3 className="font-bold mb-3 text-gray-700">{t.pot}</h3>
        <div className="flex gap-4">
          {POTS.map(pot => (<button key={pot.id} onClick={() => setConfig({ ...config, pot: pot.id })} className={`w-20 h-20 rounded-xl border-2 p-2 flex items-center justify-center ${config.pot === pot.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}><img src={`${ASSET_PATH}/${pot.file}`} alt={pot.name[language]} className="w-full h-full object-contain" /></button>))}
        </div>
      </div>
    </div>
  );

  // 6. หน้า Main Game
  return (
    <div className="min-h-screen w-full bg-zinc-800 flex items-center justify-center sm:p-4 font-sans">
      <style>{styles}</style>
      <div className="w-full max-w-[480px] h-[100dvh] sm:h-[850px] sm:max-h-[95vh] bg-white sm:rounded-[32px] shadow-2xl overflow-hidden relative flex flex-col items-center justify-between game-container-bg border-4 border-zinc-900" style={getCurrentBgStyle()}>
        {isTransforming && <div className="absolute inset-0 bg-white z-[100] animate-flash pointer-events-none"></div>}
        <div className="w-full flex justify-between items-start z-50 p-4 absolute top-0 left-0">
          <div className="relative">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="w-12 h-12 bg-white/90 rounded-full shadow-md flex items-center justify-center text-amber-800 hover:scale-105 transition-transform border border-amber-100">{isMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
            {isMenuOpen && (
              /* --- แก้ไข: เพิ่ม max-h-[60vh] และ overflow-y-auto เพื่อให้เลื่อนได้ --- */
              <div className="absolute top-14 left-0 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl p-3 flex flex-col gap-2 min-w-[180px] border border-gray-100 animate-pop-in origin-top-left z-[60] max-h-[60vh] overflow-y-auto">
                <div className="text-xs font-bold text-gray-400 px-2 mb-1">{t.menu}</div>

                <Link to="/games/matanapatha" className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded-lg text-gray-700 text-sm font-bold"><ChevronLeft size={18} /> {t.backGames}</Link>
                <Link
                  to="/games"
                  className="flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md text-amber-800 hover:bg-white hover:scale-110 transition-all border border-amber-100"
                  title="กลับหน้าหลักเว็บไซต์"
                  
                >
                  <Home className="w-5 h-5" />
                </Link>
                <div className="h-[1px] bg-gray-200 my-1"></div>
                <button onClick={() => { setView('gallery'); setIsMenuOpen(false) }} className="flex items-center gap-2 p-2 hover:bg-amber-50 rounded-lg text-amber-700 text-sm font-bold"><BookOpen size={18} /> {t.gallery}</button>
                <button onClick={() => { setView('customize'); setIsMenuOpen(false) }} className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded-lg text-purple-700 text-sm font-bold"><Palette size={18} /> {t.customize}</button>
                <button onClick={() => { setView('tutorial'); setIsMenuOpen(false) }} className="flex items-center gap-2 p-2 hover:bg-sky-50 rounded-lg text-sky-700 text-sm font-bold"><HelpCircle size={18} /> {t.howTo}</button>
                <div className="h-[1px] bg-gray-200 my-1"></div>
                <button onClick={() => { setView('intro'); setIsMenuOpen(false) }} className="flex items-center gap-2 p-2 hover:bg-pink-50 rounded-lg text-pink-700 text-sm font-bold"><Play size={18} /> {t.story}</button>
                <div className="h-[1px] bg-gray-200 my-1"></div>
                
                {/* --- Language Dropdown in Menu --- */}
                <div className="relative">
                  <button 
                    onClick={() => setShowInGameLangMenu(!showInGameLangMenu)} 
                    className="w-full flex items-center justify-between p-2 hover:bg-blue-50 rounded-lg text-blue-700 text-sm font-bold transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Languages size={18} /> {LANG_OPTIONS.find(l => l.code === language)?.label || language}
                    </div>
                    <ChevronDown size={14} className={`transition-transform ${showInGameLangMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* เมนูย่อย */}
                  {showInGameLangMenu && (
                    <div className="mt-1 ml-2 pl-2 border-l-2 border-blue-100 flex flex-col gap-1">
                       {LANG_OPTIONS.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              setLanguage(lang.code);
                              setShowInGameLangMenu(false);
                            }}
                            className={`text-left px-3 py-2 text-xs rounded-md flex items-center gap-2 hover:bg-white
                              ${language === lang.code ? 'text-blue-600 font-bold bg-white shadow-sm' : 'text-gray-500'}
                            `}
                          >
                            <span>{lang.flag}</span> {lang.label}
                          </button>
                       ))}
                    </div>
                  )}
                </div>

                <button onClick={() => setIsMuted(!isMuted)} className={`flex items-center gap-2 p-2 rounded-lg text-sm font-bold ${isMuted ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}>
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />} {isMuted ? t.soundOn : t.soundOff}
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-full border border-red-100 shadow-md"> <Heart size={20} className="text-red-500 fill-red-500 animate-pulse" /> <span className={`font-bold ${gameState.hp < 30 ? 'text-red-600 animate-bounce' : 'text-gray-700'}`}>{Math.ceil(gameState.hp)}%</span> </div>
            <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-full border border-green-100 shadow-md"> <TreeDeciduous size={20} className="text-green-600" /> <span className="font-bold text-gray-700">{Math.floor(gameState.growth)}%</span> </div>
            <button onClick={resetGame} className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-sm hover:bg-red-200"><RotateCcw size={18} /></button>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center relative w-full h-full my-2 overflow-hidden pb-48">
          {activePests.map(pest => (<div key={pest.id} onClick={() => killPest(pest.id)} className="absolute z-40 cursor-pointer active:scale-150 transition-transform" style={{ left: `${pest.x}%`, top: `${pest.y}%` }}> <div className="animate-bounce drop-shadow-lg"> <img src={`${ASSET_PATH}/pest.webp`} alt="bug" className="w-14 h-14 object-contain animate-pest-move" onError={(e) => { e.target.style.display = 'none'; }} /> </div> </div>))}
          {gameState.isNight && <Moon className="absolute top-10 right-10 text-yellow-300 animate-pulse drop-shadow-md" size={60} />}
          <div className="relative flex flex-col items-center mt-6">
            {(message || activePests.length > 0 || gameState.water > 90) && (<div className="absolute -top-16 right-0 bg-white px-4 py-2 rounded-2xl rounded-bl-none shadow-lg border-2 border-gray-300 z-50 whitespace-nowrap animate-bounce"> <p className="font-bold text-sm text-gray-700">{message || (activePests.length > 0 ? t.msg_pest : t.msg_overload_w)}</p> </div>)}
            <div className={`relative z-20 transition-all duration-500 mb-[-10px] ${!gameState.ending && !isTransforming ? 'animate-bounce' : ''}`}>
              <div className={`${isTransforming ? 'animate-spin-vanish' : 'animate-look-around'} origin-center`}>
                {!gameState.ending ? (
                  <img src={`${ASSET_PATH}/${gameState.stage}.webp`} alt={gameState.stage} className={`drop-shadow-xl object-contain animate-breathe ${gameState.stage === 'bloom' ? 'w-56' : gameState.stage === 'bush' ? 'w-48' : 'w-32'}`} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                ) : (
                  <div className="relative animate-pop-in">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-yellow-200/50 to-orange-200/50 rounded-full blur-xl animate-pulse -z-10"></div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 animate-sunburst opacity-50"> <Sparkles size={120} className="text-yellow-400" /> </div>
                    <img src={`${ASSET_PATH}/${ENDINGS[gameState.ending].img}`} alt={gameState.ending} className="w-48 drop-shadow-2xl object-contain" />
                  </div>
                )}
                <div style={{ display: 'none' }} className="text-8xl">🌱</div>
                {!gameState.ending && !isTransforming && gameState.stage !== 'seed' && (<div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 drop-shadow-md"> {gameState.hp < 40 ? <Skull size={32} className="text-gray-800" /> : activePests.length > 0 ? <Meh size={32} className="text-gray-600" /> : <Smile size={32} className="text-white" />} </div>)}
              </div>
              {gameState.ending && <div className="mt-2 bg-white/90 px-4 py-1 rounded-full text-sm font-bold shadow-sm animate-pop-in">{ENDINGS[gameState.ending].name}</div>}
            </div>
            {gameState.stage !== 'human' && gameState.stage !== 'dead' && !isTransforming && (<div className="relative z-10 mt-[-20px]"> <img src={`${ASSET_PATH}/${getCurrentPotFile()}`} alt="pot" className="w-40 h-32 object-contain drop-shadow-xl" onError={(e) => e.target.style.display = 'none'} /> </div>)}
            {gameState.stage === 'bloom' && !gameState.ending && !isTransforming && (<button onClick={() => handleAction('moon')} className="absolute top-[40%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-gradient-to-r from-indigo-500 to-purple-600 text-white w-40 h-40 rounded-full shadow-[0_0_30px_rgba(139,92,246,0.6)] animate-pulse flex flex-col items-center justify-center border-4 border-white/50 hover:scale-110 transition-transform cursor-pointer"> <Moon size={48} className="mb-2 animate-bounce" /> <span className="font-bold text-lg drop-shadow-md whitespace-pre-line text-center">{t.moon_wish}</span> </button>)}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full z-50 flex flex-col gap-2 p-4 bg-gradient-to-t from-white/90 via-white/80 to-transparent pt-8">
          {!gameState.ending && (<div className="w-full space-y-2 mb-1 bg-white/90 p-3 rounded-2xl shadow-lg backdrop-blur-sm border border-white"> <StatBar icon={<CloudRain size={16} className="text-blue-500" />} label={t.water} value={gameState.water} color={gameState.water > 90 ? "bg-red-500" : "bg-blue-500"} isWarning={gameState.water > 90} /> <StatBar icon={<Sprout size={16} className="text-green-500" />} label={t.fertilizer} value={gameState.fertilizer} color={gameState.fertilizer > 90 ? "bg-red-500" : "bg-green-500"} isWarning={gameState.fertilizer > 90} /> <StatBar icon={<Shovel size={16} className="text-amber-700" />} label={t.soil} value={gameState.soil} color={gameState.soil > 90 ? "bg-red-500" : "bg-amber-700"} isWarning={gameState.soil > 90} /> </div>)}
          <div className="w-full grid grid-cols-3 gap-3">
            {!gameState.ending ? (<> <RefillButton onClick={() => handleAction('water')} icon={<CloudRain />} label={t.action_water} color="blue" isCooldown={cooldowns.water} /> <RefillButton onClick={() => handleAction('fertilizer')} icon={<Sprout />} label={t.action_fertilizer} color="green" isCooldown={cooldowns.fertilizer} /> <RefillButton onClick={() => handleAction('soil')} icon={<Shovel />} label={t.action_soil} color="amber" isCooldown={cooldowns.soil} /> </>) : (<button onClick={resetGame} className="col-span-3 bg-white text-indigo-600 py-4 rounded-xl shadow-lg font-bold text-xl border-2 border-indigo-200 mb-safe">{t.btn_reset}</button>)}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const TutorialInfo = ({ icon, bgColor, title, desc }) => (<div className={`flex items-start gap-3 p-3 rounded-xl border ${bgColor}`}> <div className="bg-white p-2 rounded-full shadow-sm flex-shrink-0"> {icon} </div> <div> <strong className="block text-gray-800 text-sm mb-1">{title}</strong> <p className="text-gray-600 text-xs leading-relaxed">{desc}</p> </div> </div>);
const StatBar = ({ icon, value, color, isWarning }) => (<div className="flex items-center gap-2"> <div className={`w-5 flex justify-center ${isWarning ? 'animate-bounce' : ''}`}>{icon}</div> <div className="flex-1 relative h-3 bg-gray-200 rounded-full overflow-hidden border border-gray-300 shadow-inner"> <div className="absolute top-0 bottom-0 right-0 w-[10%] bg-red-50/50 z-0 border-l border-red-300"></div> <div className="absolute top-0 bottom-0 left-[90%] w-[2px] bg-red-600 z-20 shadow-sm"></div> <div className={`h-full ${color} transition-all duration-300 relative z-10`} style={{ width: `${value}%` }}></div> </div> {isWarning && <div className="text-[9px] text-red-500 font-bold bg-white px-1 rounded border border-red-200 min-w-[24px] text-center">เกิน</div>} </div>);
const RefillButton = ({ onClick, icon, label, color, isCooldown }) => {
  const colors = { blue: { bg: 'bg-blue-50', fill: 'bg-blue-300', text: 'text-blue-600', border: 'border-blue-200' }, green: { bg: 'bg-green-50', fill: 'bg-green-300', text: 'text-green-600', border: 'border-green-200' }, amber: { bg: 'bg-amber-50', fill: 'bg-amber-400', text: 'text-amber-700', border: 'border-amber-200' } };
  const c = colors[color];
  return (<button onClick={onClick} disabled={isCooldown} className={`relative flex flex-col items-center justify-center h-20 rounded-xl border-b-4 transition-all shadow-sm overflow-hidden ${c.bg} ${c.border} ${c.text} ${!isCooldown ? 'active:border-b-0 active:translate-y-1 hover:brightness-95' : 'cursor-wait'}`}> <div className={`absolute bottom-0 left-0 w-full z-0 wave-bg ${c.fill} ${isCooldown ? 'animate-refill' : 'h-full opacity-20'}`}></div> <div className="relative z-10 flex flex-col items-center transform transition-transform duration-300"> <div className={`mb-1 transform scale-100 ${isCooldown ? 'opacity-50' : 'animate-bounce'}`}>{icon}</div> <span className="text-xs font-bold mt-1">{isCooldown ? '...' : label}</span> </div> </button>);
};

export default MattanaGame;